import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFleetData } from "./FleetDataContext";
import { supabase } from "./supabaseClient";
import Nav from "./Nav";
import { getShipStatus, SHIP_STATUS, SHIP_STATUS_LABELS } from "./FleetFilterBar";

const ACCENT = "#ce93d8";

// Optimized image component: only loads when scrolled into view, shows placeholder, fades in
function LazyImage({ src, alt, style, thumbnailWidth }) {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Append Supabase image transform if it's a Supabase storage URL
  const optimizedSrc = src && thumbnailWidth && src.includes("/storage/v1/object/")
    ? `${src}?width=${thumbnailWidth}&resize=contain`
    : src;

  return (
    <div ref={imgRef} style={{ ...style, position: "relative", overflow: "hidden" }}>
      {/* Placeholder */}
      {!loaded && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(206,147,216,0.04)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 28, color: "rgba(206,147,216,0.1)" }}>◇</div>
        </div>
      )}
      {inView && (
        <img
          src={optimizedSrc}
          alt={alt}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", opacity: loaded ? 1 : 0, transition: "opacity 0.35s ease" }}
        />
      )}
    </div>
  );
}

function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const stars = Array.from({ length: 200 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      a: Math.random(), speed: Math.random() * 0.003 + 0.001,
    }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.a += s.speed;
        if (s.a > 1) s.a = 0;
        const alpha = Math.sin(s.a * Math.PI) * 0.7 + 0.1;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", pointerEvents: "none", zIndex: 0 }} />;
}

function SectionLabel({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <div style={{ width: 32, height: 2, background: ACCENT, flexShrink: 0 }} />
      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: ACCENT, textTransform: "uppercase" }}>{children}</span>
    </div>
  );
}

// Discord role id required for "Member" gating — mirrors App.js MEMBER_ROLE_ID.
const MEMBER_ROLE_ID = "1303561707196649512";

// ─────────────────────────────────────────────────────────────────────────────
// SHIP DETAIL VIEW — uses pre-loaded data from parent, no extra fetches
// ─────────────────────────────────────────────────────────────────────────────
function ShipDetail({ shipId, onBack, allShips, allProfiles }) {
  const { profile } = useAuth();
  // Member gating for the owner list. Honors member_status as the canonical
  // membership signal (the discord_roles snapshot can lag behind reality).
  const viewerIsMember = !!profile && (
    profile.member_status === 'member' ||
    profile.is_admin ||
    (Array.isArray(profile.discord_roles) && profile.discord_roles.includes(MEMBER_ROLE_ID))
  );
  // Derive ship and owners from pre-loaded parent data — instant, no network call
  const ship = useMemo(() => (allShips || []).find(s => s.id === shipId) || null, [allShips, shipId]);

  const owners = useMemo(() => {
    if (!ship) return [];
    const found = [];
    (allProfiles || []).forEach(p => {
      const ships = Array.isArray(p.ships) ? p.ships : [];
      ships.forEach(entry => {
        if (typeof entry === "object" && entry !== null && entry.ship_id === shipId) {
          // Carry the status onto the owner record so the detail view can
          // render a Pledged/In-Game pill next to each owner.
          found.push({ ...p, quantity: entry.quantity || 1, ship_status: getShipStatus(entry) });
        }
      });
    });
    return found;
  }, [allProfiles, shipId, ship]);

  if (!ship) {
    return (
      <div style={{ textAlign: "center", padding: "120px 0" }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: "rgba(255,255,255,0.2)", marginBottom: 20 }}>SHIP NOT FOUND</div>
        <button onClick={onBack} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "8px 20px", border: `1px solid ${ACCENT}`, color: ACCENT, background: "transparent", cursor: "pointer" }}>
          Back to Fleet
        </button>
      </div>
    );
  }

  const totalOwned = owners.reduce((sum, o) => sum + (o.quantity || 1), 0);

  return (
    <div>
      {/* Back button */}
      <button onClick={onBack} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "8px 20px", border: `1px solid rgba(206,147,216,0.3)`, color: "rgba(232,234,246,0.5)", background: "transparent", cursor: "pointer", marginBottom: 32, transition: "all 0.2s" }}
        onMouseEnter={e => { e.currentTarget.style.color = ACCENT; e.currentTarget.style.borderColor = ACCENT; }}
        onMouseLeave={e => { e.currentTarget.style.color = "rgba(232,234,246,0.5)"; e.currentTarget.style.borderColor = "rgba(206,147,216,0.3)"; }}>
        &larr; Back to Fleet
      </button>

      {/* Ship header */}
      <div style={{ display: "flex", gap: 40, flexWrap: "wrap", marginBottom: 48 }}>
        {/* Ship image */}
        <div style={{ width: "100%", maxWidth: 480, flexShrink: 0 }}>
          <div style={{ width: "100%", aspectRatio: "16/9", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(206,147,216,0.15)", overflow: "hidden", position: "relative" }}>
            {ship.image_url ? (
              <LazyImage src={ship.image_url} alt={ship.name} thumbnailWidth={960} style={{ width: "100%", height: "100%" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 48, color: "rgba(206,147,216,0.15)" }}>
                ◇
              </div>
            )}
          </div>
        </div>

        {/* Ship info */}
        <div style={{ flex: 1, minWidth: 260 }}>
          {ship.manufacturer && (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: ACCENT, textTransform: "uppercase", marginBottom: 8 }}>
              {ship.manufacturer}
            </div>
          )}
          <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(24px, 4vw, 42px)", color: "#e8eaf6", marginBottom: 16 }}>
            {ship.name}
          </h1>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
            <div style={{ padding: "16px 18px", border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", marginBottom: 6 }}>Owners</div>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: "#e8eaf6" }}>{owners.length}</div>
            </div>
            <div style={{ padding: "16px 18px", border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.02)" }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", marginBottom: 6 }}>Total In Org</div>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: "#e8eaf6" }}>{totalOwned}</div>
            </div>
          </div>

          {ship.pledge_url && (
            <a href={ship.pledge_url} target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", padding: "10px 22px", border: `2px solid ${ACCENT}`, color: ACCENT, background: "transparent", cursor: "pointer", transition: "all 0.2s", textDecoration: "none", clipPath: "polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)" }}
              onMouseEnter={e => { e.currentTarget.style.background = ACCENT; e.currentTarget.style.color = "#05070f"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ACCENT; }}>
              View on Pledge Store &rarr;
            </a>
          )}
        </div>
      </div>

      {/* Owners list — names visible only to org members. Non-members see count + a hint. */}
      <SectionLabel>Owners</SectionLabel>
      <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 22, color: "#e8eaf6", marginBottom: 24 }}>
        {owners.length} {owners.length === 1 ? "Member" : "Members"}
      </h2>

      {owners.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
          NO OWNERS YET
        </div>
      ) : !viewerIsMember ? (
        <div style={{ textAlign: "center", padding: "32px 24px", border: "1px dashed rgba(206,147,216,0.25)", background: "rgba(206,147,216,0.04)" }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", marginBottom: 8 }}>
            Members Only
          </div>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.55)", lineHeight: 1.5 }}>
            Sign in with the Member role in our Discord to see who owns this ship.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {owners.map(owner => (
            <Link key={owner.id} to={`/profile/${owner.id}`} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)", transition: "all 0.2s", textDecoration: "none", color: "inherit" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `${ACCENT}44`; e.currentTarget.style.background = `${ACCENT}08`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", border: "2px solid rgba(206,147,216,0.3)", flexShrink: 0 }}>
                {owner.discord_avatar ? (
                  <img src={owner.discord_avatar} alt={owner.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", background: "#0d1120", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron'", fontSize: 16, color: ACCENT }}>
                    {owner.discord_username?.charAt(0)}
                  </div>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 16, color: "#e8eaf6" }}>{owner.discord_username}</div>
                {owner.org_role && (
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>{owner.org_role}</div>
                )}
              </div>
              {owner.quantity > 1 && (
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, fontWeight: 700, color: ACCENT, background: `${ACCENT}15`, padding: "4px 12px", border: `1px solid ${ACCENT}33` }}>
                  ×{owner.quantity}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FLEET GRID VIEW
// ─────────────────────────────────────────────────────────────────────────────
function FleetGrid({ ships, profiles, ownerDataCache, onSelectShip, title, subtitle, viewMode, onViewModeChange, teams }) {
  const [search, setSearch] = useState("");
  const [filterManufacturer, setFilterManufacturer] = useState("");
  // Pledged / In-Game filter. Default 'all' so the view counts everything.
  const [fleetStatus, setFleetStatus] = useState("all");

  // ── Event utilization counts (used to pin top ships at the start of the grid) ──
  // flagshipCount[ship_id] = number of events where this ship was tagged as flagship
  // vehicleCount[ship_id]  = sum of quantity_requested across all event_group_ships rows
  const [eventUsage, setEventUsage] = useState({ flagshipCount: {}, vehicleCount: {}, loaded: false });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: flagships }, { data: groupShips }] = await Promise.all([
        supabase.from("event_flagships").select("ship_id"),
        supabase.from("event_group_ships").select("ship_id, quantity_requested"),
      ]);
      if (cancelled) return;
      const flagshipCount = {};
      (flagships || []).forEach(f => {
        if (!f.ship_id) return;
        flagshipCount[f.ship_id] = (flagshipCount[f.ship_id] || 0) + 1;
      });
      const vehicleCount = {};
      (groupShips || []).forEach(g => {
        if (!g.ship_id) return;
        vehicleCount[g.ship_id] = (vehicleCount[g.ship_id] || 0) + (Number(g.quantity_requested) || 0);
      });
      setEventUsage({ flagshipCount, vehicleCount, loaded: true });
    })();
    return () => { cancelled = true; };
  }, []);

  // Top 5 most-utilized flagships, then top 10 other vehicles (excluding the flagship 5)
  const { topFlagships, topVehicles, flagshipRank, vehicleRank } = useMemo(() => {
    const { flagshipCount, vehicleCount } = eventUsage;
    const flagshipRanked = Object.entries(flagshipCount)
      .filter(([, c]) => c > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([id]) => id);
    const flagshipSet = new Set(flagshipRanked);
    const vehicleRanked = Object.entries(vehicleCount)
      .filter(([id, c]) => c > 0 && !flagshipSet.has(id))
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([id]) => id);
    return {
      topFlagships: flagshipRanked,
      topVehicles: vehicleRanked,
      flagshipRank: new Map(flagshipRanked.map((id, i) => [id, i])),
      vehicleRank:  new Map(vehicleRanked.map((id, i) => [id, i])),
    };
  }, [eventUsage]);

  // Use pre-computed owner data from parent ONLY when no status filter is in
  // effect. The pre-computed cache doesn't know about status, so we have to
  // re-aggregate locally whenever the filter is something other than 'all'.
  const ownerData = useMemo(() => {
    if (ownerDataCache && fleetStatus === "all") return ownerDataCache;
    const data = {};
    ships.forEach(s => { data[s.id] = { owners: 0, totalCount: 0 }; });
    (profiles || []).forEach(p => {
      const userShips = Array.isArray(p.ships) ? p.ships : [];
      userShips.forEach(entry => {
        if (typeof entry === "object" && entry !== null && entry.ship_id && data[entry.ship_id]) {
          if (fleetStatus !== "all" && getShipStatus(entry) !== fleetStatus) return;
          data[entry.ship_id].owners += 1;
          data[entry.ship_id].totalCount += (entry.quantity || 1);
        }
      });
    });
    return data;
  }, [ships, profiles, ownerDataCache, fleetStatus]);

  // Get unique manufacturers for filter — only those with at least one owner
  const manufacturers = useMemo(() => {
    const owned = ships.filter(s => ownerData[s.id] && ownerData[s.id].owners > 0);
    return [...new Set(owned.map(s => s.manufacturer).filter(Boolean))].sort();
  }, [ships, ownerData]);

  // Filter ships — only show ships that have at least one owner
  const filtered = ships.filter(s => {
    const data = ownerData[s.id];
    if (!data || data.owners === 0) return false;
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.manufacturer || "").toLowerCase().includes(search.toLowerCase());
    const matchMfr = !filterManufacturer || s.manufacturer === filterManufacturer;
    return matchSearch && matchMfr;
  });

  // Sort: pinned flagships (in rank order) → pinned vehicles (in rank order) → alphabetical
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aFlag = flagshipRank.get(a.id);
      const bFlag = flagshipRank.get(b.id);
      if (aFlag !== undefined && bFlag !== undefined) return aFlag - bFlag;
      if (aFlag !== undefined) return -1;
      if (bFlag !== undefined) return 1;
      const aVeh = vehicleRank.get(a.id);
      const bVeh = vehicleRank.get(b.id);
      if (aVeh !== undefined && bVeh !== undefined) return aVeh - bVeh;
      if (aVeh !== undefined) return -1;
      if (bVeh !== undefined) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [filtered, flagshipRank, vehicleRank]);

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <SectionLabel>{subtitle || "Organization Fleet"}</SectionLabel>
        <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(28px, 4vw, 48px)", color: "#e8eaf6", marginBottom: 8 }}>
          {title || "Fleet"}
        </h1>
        <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 17, color: "rgba(232,234,246,0.45)" }}>
          {filtered.length} ships owned across {(profiles || []).length} org members.
        </p>
      </div>

      {/* Fleet view dropdown — replaces the old horizontal tab strip */}
      {onViewModeChange && (() => {
        const teamObj = (teams || []).find(t => t.name === viewMode);
        const dropdownColor = viewMode === "org" ? ACCENT : (teamObj?.color || ACCENT);
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ position: "relative", display: "inline-block" }}>
              <select
                value={viewMode}
                onChange={e => onViewModeChange(e.target.value)}
                style={{
                  appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
                  fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700,
                  letterSpacing: 2, textTransform: "uppercase",
                  background: `${dropdownColor}12`, color: dropdownColor,
                  border: `1px solid ${dropdownColor}`,
                  padding: "10px 38px 10px 18px", cursor: "pointer", minWidth: 220,
                  transition: "all 0.2s",
                }}
              >
                <option value="org" style={{ background: "#05070f", color: "#e8eaf6" }}>Org Fleet</option>
                {(teams || []).map(t => (
                  <option key={t.id} value={t.name} style={{ background: "#05070f", color: "#e8eaf6" }}>
                    {t.name}
                  </option>
                ))}
              </select>
              <span aria-hidden style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: dropdownColor, fontSize: 10 }}>▼</span>
            </div>
          </div>
        );
      })()}

      {/* Search & Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 32, flexWrap: "wrap", alignItems: "center" }}>
        <input type="text" placeholder="Search ships..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(206,147,216,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2 }} />
        <select value={filterManufacturer} onChange={e => setFilterManufacturer(e.target.value)}
          style={{ padding: "10px 14px", background: "#0a0610", border: "1px solid rgba(206,147,216,0.25)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, minWidth: 160 }}>
          <option value="">All Manufacturers</option>
          {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        {/* Pledged / In-Game segmented control — recomputes owner counts in place */}
        <div style={{ display: "flex", border: "1px solid rgba(206,147,216,0.25)" }}>
          {[
            { key: "all", label: "All" },
            { key: SHIP_STATUS.PLEDGED, label: SHIP_STATUS_LABELS.pledged },
            { key: SHIP_STATUS.IN_GAME, label: SHIP_STATUS_LABELS.in_game },
          ].map((opt, i) => {
            const active = fleetStatus === opt.key;
            return (
              <button key={opt.key} type="button" onClick={() => setFleetStatus(opt.key)}
                style={{
                  padding: "10px 14px",
                  fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
                  background: active ? `${ACCENT}18` : "transparent",
                  color: active ? ACCENT : "rgba(232,234,246,0.5)",
                  border: "none",
                  borderRight: i < 2 ? "1px solid rgba(206,147,216,0.15)" : "none",
                  cursor: "pointer",
                }}>
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        {[
          { label: "Ships", value: filtered.length },
          { label: "Manufacturers", value: manufacturers.length },
          { label: "Total Owned", value: Object.values(ownerData).reduce((s, d) => s + d.totalCount, 0) },
        ].map(stat => (
          <div key={stat.label} style={{ padding: "16px 24px", border: "1px solid rgba(206,147,216,0.15)", background: `${ACCENT}06`, flex: "1 1 120px", minWidth: 120 }}>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: 28, color: ACCENT, lineHeight: 1, marginBottom: 4 }}>{stat.value}</div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase" }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Ship grid */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 0", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
          {search || filterManufacturer ? "NO SHIPS MATCH YOUR FILTERS" : "NO OWNED SHIPS IN REGISTRY"}
        </div>
      ) : (
        <div className="fleet-grid">
          {sorted.map(ship => {
            const data = ownerData[ship.id] || { owners: 0, totalCount: 0 };
            const flagshipUses = eventUsage.flagshipCount[ship.id] || 0;
            const vehicleUses  = eventUsage.vehicleCount[ship.id] || 0;
            const isPinnedFlag = flagshipRank.has(ship.id);
            const isPinnedVeh  = vehicleRank.has(ship.id);
            // Color the card border subtly when it's pinned, so the top-of-list ships stand out
            const baseBorder = isPinnedFlag ? "rgba(252,214,87,0.45)" : isPinnedVeh ? "rgba(79,195,247,0.35)" : "rgba(206,147,216,0.15)";
            return (
              <div key={ship.id} onClick={() => onSelectShip(ship.id)} style={{ border: `1px solid ${baseBorder}`, background: "rgba(255,255,255,0.02)", cursor: "pointer", transition: "all 0.25s", overflow: "hidden", clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)", position: "relative" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = `${ACCENT}55`; e.currentTarget.style.background = `${ACCENT}08`; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = baseBorder; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                {/* Ship image + badge */}
                <div style={{ position: "relative", width: "100%", aspectRatio: "16/9", background: "rgba(0,0,0,0.3)", overflow: "hidden" }}>
                  {ship.image_url ? (
                    <LazyImage src={ship.image_url} alt={ship.name} thumbnailWidth={480} style={{ width: "100%", height: "100%" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 36, color: "rgba(206,147,216,0.12)" }}>
                      ◇
                    </div>
                  )}
                  {/* Utilization badge (top-left) — only shown for pinned ships */}
                  {isPinnedFlag && (
                    <div className="ship-badge ship-badge-left" style={{ position: "absolute", top: 8, left: 8, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "4px 10px", border: "1px solid rgba(252,214,87,0.55)", display: "flex", alignItems: "center", gap: 6 }}>
                      <span className="ship-badge-label-hide-mobile" style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "#fcd657" }}>★ FLAGSHIP</span>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#fcd657" }}>★</span>
                      <span className="ship-badge-num" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: "#fcd657" }}>×{flagshipUses}</span>
                    </div>
                  )}
                  {!isPinnedFlag && isPinnedVeh && (
                    <div className="ship-badge ship-badge-left" style={{ position: "absolute", top: 8, left: 8, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "4px 10px", border: "1px solid rgba(79,195,247,0.5)", display: "flex", alignItems: "center", gap: 6 }}>
                      <span className="ship-badge-label-hide-mobile" style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "#4fc3f7" }}>EVENT FLEET</span>
                      <span className="ship-badge-num" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: "#4fc3f7" }}>×{vehicleUses}</span>
                    </div>
                  )}
                  <div className="ship-badge ship-badge-right" style={{ position: "absolute", top: 8, right: 8, background: "rgba(5,7,15,0.85)", backdropFilter: "blur(8px)", padding: "4px 10px", border: `1px solid ${ACCENT}44` }}>
                    <span className="ship-badge-num" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: ACCENT }}>{data.totalCount}</span>
                    <span className="ship-badge-label-hide-mobile" style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", marginLeft: 4 }}>IN USE</span>
                  </div>
                </div>
                {/* Ship info */}
                <div className="ship-card-content" style={{ padding: "16px 18px" }}>
                  <div className="ship-name" style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 14, color: "#e8eaf6", marginBottom: 4, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{ship.name}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                    <div className="ship-meta" style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ship.manufacturer || "Unknown"}</div>
                    <div className="ship-meta" style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: `${ACCENT}99`, flexShrink: 0 }}>
                      {data.owners} {data.owners === 1 ? "owner" : "owners"}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN FLEET PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function FleetPage() {
  const { shipId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Consume pre-loaded fleet data from context — no fetching needed
  const {
    ships, profiles, teams,
    loaded, loading,
    ownerDataCache, teamProfilesCache,
  } = useFleetData();

  const [viewMode, setViewMode] = useState("org"); // "org" or team name
  const [selectedShip, setSelectedShip] = useState(shipId || null);

  useEffect(() => {
    setSelectedShip(shipId || null);
  }, [shipId]);

  // Preserve grid scroll across a card-click → detail → Back round trip.
  // Save when entering detail, restore when returning to the grid view.
  const SCROLL_KEY = "fleetGridScrollY";
  const handleSelectShip = useCallback((id) => {
    try { sessionStorage.setItem(SCROLL_KEY, String(window.scrollY)); } catch (e) {}
    setSelectedShip(id);
    navigate(`/fleet/${id}`);
  }, [navigate]);

  // Use in-app history when available so a back from a ship detail returns
  // to the actual referring page (profile/team/etc.) instead of always
  // shoving the user onto the org-wide /fleet view. Deep-linked visits with
  // no in-app history fall back to /fleet.
  const handleBack = useCallback(() => {
    setSelectedShip(null);
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) navigate(-1);
    else navigate("/fleet");
  }, [navigate]);

  // When we land back on the grid view, restore the saved scroll. rAF waits
  // for the grid render, the 200ms timeout catches LazyImage layout shifts.
  useEffect(() => {
    if (selectedShip != null) return;
    let saved;
    try { saved = sessionStorage.getItem(SCROLL_KEY); } catch (e) { return; }
    if (saved == null) return;
    const y = parseInt(saved, 10);
    if (!Number.isFinite(y)) return;
    try { sessionStorage.removeItem(SCROLL_KEY); } catch (e) {}
    const raf = requestAnimationFrame(() => window.scrollTo(0, y));
    const t = setTimeout(() => window.scrollTo(0, y), 200);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [selectedShip]);

  if (!loaded || loading) {
    return (
      <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: `${ACCENT}99` }}>
        LOADING...
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: "#05070f", color: "#e8eaf6", minHeight: "100vh", overflowX: "hidden" }}>
      <StarField />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: #ce93d8; color: #05070f; }
        a { text-decoration: none; color: inherit; }
        .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; }
        .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: #ce93d8; transition: width 0.3s ease; }
        .nav-link:hover { color: #ce93d8; }
        .nav-link:hover::after { width: 100%; }
        .scanline { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); }
        input, select, textarea { color-scheme: dark; }
        input:focus, select:focus, textarea:focus { outline: none; border-color: ${ACCENT} !important; }
        /* Ship card grid — wider min on desktop, collapses to ~2 cards across on mobile portrait. */
        .fleet-grid { display: grid; gap: 16px; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
        @media (max-width: 700px) {
          .fleet-grid { gap: 8px; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
          .ship-card-content { padding: 8px 10px !important; }
          .ship-name { font-size: 11px !important; line-height: 1.25 !important; }
          .ship-meta { font-size: 8px !important; }
          .ship-badge { font-size: 7px !important; padding: 2px 5px !important; gap: 4px !important; top: 5px !important; }
          .ship-badge-left { left: 5px !important; }
          .ship-badge-right { right: 5px !important; }
          .ship-badge-num { font-size: 9px !important; }
          .ship-badge-label-hide-mobile { display: none !important; }
        }
      `}</style>
      <div className="scanline" />
      <Nav accentColor={ACCENT} />

      {/* Content */}
      <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: `${selectedShip ? "112px" : "calc(64px + env(safe-area-inset-top, 0px) + 32px)"} clamp(16px, 4vw, 40px) 48px` }}>
        {selectedShip ? (
          <ShipDetail shipId={selectedShip} onBack={handleBack} allShips={ships} allProfiles={profiles} />
        ) : viewMode === "org" ? (
          <FleetGrid ships={ships} profiles={profiles} ownerDataCache={ownerDataCache} onSelectShip={handleSelectShip} title="Org Fleet" subtitle="Starkeeper Industries"
            viewMode={viewMode} onViewModeChange={setViewMode} teams={teams} />
        ) : (
          <FleetGrid ships={ships} profiles={teamProfilesCache[viewMode] || []} ownerDataCache={null} onSelectShip={handleSelectShip} title={`${viewMode} Fleet`} subtitle="Team Fleet"
            viewMode={viewMode} onViewModeChange={setViewMode} teams={teams} />
        )}
      </div>

      {/* Footer */}
      <footer style={{ position: "relative", zIndex: 1, borderTop: "1px solid rgba(255,255,255,0.07)", padding: "32px 40px", background: "rgba(0,0,0,0.4)", marginTop: 60 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.2, color: "rgba(232,234,246,0.18)", lineHeight: 1.8 }}>
          &copy; 2026 Starkeeper Industries. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

// Export sub-components for reuse in TeamPage
export { FleetGrid, ShipDetail, LazyImage };
